/**
 * DancerMediaService
 *
 * Service for managing dancer media uploads (headshots, photos, videos) for profile wizard
 * Follows ReimbursementDocumentService pattern
 */
import { createHash } from 'node:crypto';
import type { SupabaseClient } from '@supabase/supabase-js';

import { isTransientNetworkError } from '@kit/shared/lib/is-transient-network-error';
import { captureServiceError } from '@kit/shared/lib/sentry-helpers';
import { fetchVideoMeta } from '@kit/shared/lib/video-oembed';
import { Result, ServiceError } from '@kit/shared/result';
import { BaseService } from '@kit/shared/services';
import {
  createR2ServiceFromEnv,
  createStorageUrlService,
  MediaWriteService,
  SignedUrlExpiry,
  StorageBuckets,
  type R2Service,
  type StorageUrlService,
} from '@kit/shared/storage';
import {
  createMuxVideoServiceFromEnv,
  type VideoService,
} from '@kit/shared/video';
import type { Database } from '@kit/supabase/database';

import { looksLikePdf, PDF_SNIFF_BYTES } from '../lib/pdf-signature';
import {
  buildMediaMetadataUpdates,
  buildMediaStoragePath,
  isValidExternalVideoUrl,
  normalizeContentHash,
  normalizeExternalVideoUrl,
  resolveAllowedMimeTypes,
  resolveMaxUploadSize,
} from './dancer-media.mappers';
import type {
  DancerMedia,
  FinalizeDirectUploadInput,
  MediaType,
  MediaUploadInput,
  VideoLinkInput,
} from './dancer-media.schema';

export type {
  CropRectInput,
  DancerMedia,
  FinalizeDirectUploadInput,
  MediaUploadInput,
  VideoLinkInput,
} from './dancer-media.schema';
export {
  CROP_ASPECT,
  CROP_ASPECT_TOLERANCE,
  validateCropRect,
} from './dancer-media.schema';

/**
 * Finalise-write retry budget (BAL-696). Total attempts of the idempotent
 * upsert; only transient/timeout failures are retried, with a linear backoff
 * of `FINALIZE_RETRY_BASE_MS * attempt`.
 */
const FINALIZE_MAX_ATTEMPTS = 3;
const FINALIZE_RETRY_BASE_MS = 200;

/**
 * Read-call ceiling for {@link DancerMediaService.readStorageHead} (BAL-747).
 * Generous next to the ~1 KB it wants — real chunks are 16-64 KB, so one or two
 * reads cover it. This exists only so a stream of zero-length chunks cannot
 * loop forever.
 */
const MAX_HEAD_READS = 64;

/**
 * Map an uploaded video's MIME type to a file extension for its R2 origin key
 * (BAL-721). Keeps the key URL-safe (no user-controlled file_name); Mux ingests
 * any of these containers/codecs regardless of the extension we choose.
 */
const VIDEO_MIME_EXT: Record<string, string> = {
  'video/mp4': 'mp4',
  'video/quicktime': 'mov',
  'video/webm': 'webm',
};
const DEFAULT_VIDEO_EXT = 'mp4';

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Service for managing dancer media uploads
 */
export class DancerMediaService extends BaseService {
  private storageService: StorageUrlService;
  private readonly videoServiceOverride: VideoService | null;
  private readonly r2ServiceOverride: R2Service | null;

  /**
   * @param deps Optional injected collaborators. In production these are left
   * undefined and resolved lazily from env on first native-video ingest, so
   * the (far more common) photo/headshot path never touches the Mux/R2 env.
   * Tests inject mocks to exercise `finalizeDirectUpload` without live vendors.
   */
  constructor(
    client: SupabaseClient<Database>,
    deps?: { videoService?: VideoService; r2Service?: R2Service },
  ) {
    super(client);
    this.storageService = createStorageUrlService(client);
    this.videoServiceOverride = deps?.videoService ?? null;
    this.r2ServiceOverride = deps?.r2Service ?? null;
  }

  /** Lazily resolve the video-delivery vendor (Mux behind the Rule-11 boundary). */
  private getVideoService(): VideoService {
    return this.videoServiceOverride ?? createMuxVideoServiceFromEnv();
  }

  /** Lazily resolve the R2 origin store (the single R2 edit surface, Rule 11). */
  private getR2Service(): R2Service {
    return this.r2ServiceOverride ?? createR2ServiceFromEnv();
  }

  /**
   * Upload media file to storage and create database record
   * Upload a media file (headshot, photo, or video) to dancer-media storage
   * and create the matching artist_media row. For external video URLs
   * (YouTube/Vimeo), use addVideoLink() instead.
   */
  async uploadMedia(
    userId: string,
    file: File,
    input: MediaUploadInput,
  ): Promise<Result<DancerMedia>> {
    const logContext = {
      method: 'uploadMedia',
      userId,
      mediaType: input.mediaType,
      fileName: file.name,
      fileSize: file.size,
      mimeType: file.type,
    };

    this.log('info', 'Starting media upload', logContext);

    try {
      const maxSize = resolveMaxUploadSize(input.mediaType);

      if (file.size > maxSize) {
        this.log('warn', 'File size exceeds limit', {
          ...logContext,
          maxSize,
        });
        return Result.fail(
          ServiceError.validation(
            `File size exceeds ${maxSize / (1024 * 1024)}MB limit`,
          ),
        );
      }

      const { allowedTypes, allowedDescription } = resolveAllowedMimeTypes(
        input.mediaType,
      );

      if (!allowedTypes.includes(file.type)) {
        this.log('warn', 'Invalid file type', {
          ...logContext,
          allowedTypes,
        });
        return Result.fail(
          ServiceError.validation(
            `Invalid file type. Allowed: ${allowedDescription}`,
          ),
        );
      }

      // Validate headshot uniqueness
      if (input.mediaType === 'headshot') {
        const { data: existingHeadshot } = await this.client
          .from('artist_media')
          .select('id')
          .eq('user_id', userId)
          .eq('media_type', 'headshot')
          .maybeSingle();

        if (existingHeadshot) {
          this.log('warn', 'Headshot already exists', {
            ...logContext,
            existingHeadshotId: existingHeadshot.id,
          });
          return Result.fail(
            ServiceError.validation(
              'Headshot already exists. Please delete the existing one first.',
            ),
          );
        }
      }

      // Validate video count cap: 5 videos total per user (combined
      // YouTube/Vimeo links + uploaded files). Mirrors the same limit
      // enforced in addVideoLink() below.
      if (input.mediaType === 'video') {
        const { data: existingVideos } = await this.client
          .from('artist_media')
          .select('id')
          .eq('user_id', userId)
          .eq('media_type', 'video');

        if (existingVideos && existingVideos.length >= 5) {
          this.log('warn', 'Max videos reached', {
            ...logContext,
            existingVideoCount: existingVideos.length,
          });
          return Result.fail(
            ServiceError.validation('Maximum 5 videos allowed per profile.'),
          );
        }
      }

      // Content hash (BAL-700): the server receives the raw bytes on this
      // path, so compute the md5 directly — matches the S3 single-PUT ETag
      // that finalizeDirectUpload reads from storage, so both upload paths
      // populate content_hash identically. Persisted on the row and enforced
      // by idx_artist_media_unique_content_hash (same-bytes dedup even under
      // a filename change).
      const contentHash = createHash('md5')
        .update(Buffer.from(await file.arrayBuffer()))
        .digest('hex');

      // Idempotent re-upload guard (BAL-700): a caller re-uploading a file it
      // already uploaded (double-click, retry-after-timeout, drag the same
      // file twice) previously always inserted a new artist_media row with a
      // distinct storage_path. Return the existing live row instead of
      // creating a duplicate. Matches on filename OR identical bytes
      // (content_hash) so a re-upload saved under a different name is caught
      // too. Bounded to at most two lookups, no N+1.
      const existingMedia = await this.findExistingUploadedMedia(
        userId,
        input.mediaType,
        file.name,
        contentHash,
      );
      if (existingMedia) {
        this.log('info', 'Duplicate upload detected — returning existing row', {
          ...logContext,
          existingMediaId: existingMedia.id,
        });
        const existingSignedUrlResult = existingMedia.storage_path
          ? await this.getSignedUrl(
              existingMedia.storage_path,
              SignedUrlExpiry.IMMEDIATE_DISPLAY,
            )
          : null;
        const existingDisplayUrl = existingSignedUrlResult?.isSuccess
          ? existingSignedUrlResult.getValue()
          : null;
        return Result.ok({
          ...existingMedia,
          storage_path: existingDisplayUrl || existingMedia.storage_path,
        });
      }

      const storagePath = buildMediaStoragePath(
        userId,
        input.mediaType,
        file.name,
      );

      this.log('info', 'Uploading file to storage', {
        ...logContext,
        storagePath,
      });

      // Upload bytes then insert the row atomically. MediaWriteService rolls
      // back the just-uploaded object if the insert fails (no orphan object).
      // The row payload (incl. the professional_profile_id lookup) is built
      // INSIDE writeRow so it runs after the upload, preserving the original
      // upload-then-record ordering.
      const recordResult = await new MediaWriteService(
        this.client,
      ).uploadAndRecord<Database['public']['Tables']['artist_media']['Row']>({
        bucket: StorageBuckets.DANCER_MEDIA,
        path: storagePath,
        body: file,
        uploadOptions: { contentType: file.type, upsert: false },
        writeRow: async (verifiedPath) => {
          this.log('info', 'Creating database record', {
            ...logContext,
            userId,
            storagePath: verifiedPath,
            mediaType: input.mediaType,
          });

          // The row contract (columns, professional_profile_id link, headshot
          // is_primary + profile stamp) lives in the shared
          // finalize_dancer_media_upload RPC (BAL-603, Rule 8) — the same call
          // finalizeDirectUpload and the mobile app make.
          const rpcArgs = {
            p_user_id: userId,
            p_media_type: input.mediaType,
            p_storage_path: verifiedPath,
            p_file_name: file.name,
            p_file_size_bytes: file.size,
            p_mime_type: file.type,
            p_content_hash: contentHash,
            p_title: input.title || undefined,
            p_description: input.description || undefined,
          };

          const { data: media, error: dbError } = await this.client.rpc(
            'finalize_dancer_media_upload',
            rpcArgs,
          );

          if (dbError || !media) {
            this.log('error', 'Database insert failed', {
              ...logContext,
              error: dbError?.message,
              errorCode: dbError?.code,
              errorDetails: dbError?.details,
              errorHint: dbError?.hint,
              rpcArgs,
            });
            return Result.fail(
              ServiceError.database(
                dbError?.message ?? 'Failed to save media',
                dbError,
              ),
            );
          }

          return Result.ok(media);
        },
      });

      if (recordResult.isFailure) {
        return Result.fail(recordResult.getError());
      }

      const media = recordResult.getValue();

      this.log('info', 'Media upload completed successfully', {
        ...logContext,
        mediaId: media.id,
      });

      // Generate signed URL for immediate display in the uploader
      const signedUrlResult = await this.getSignedUrl(
        storagePath,
        SignedUrlExpiry.IMMEDIATE_DISPLAY,
      );
      const displayUrl = signedUrlResult.isSuccess
        ? signedUrlResult.getValue()
        : null;

      // Return media with signed URL as storage_path for client display
      return Result.ok({
        ...media,
        storage_path: displayUrl || storagePath,
      });
    } catch (error) {
      const serviceError = ServiceError.internal(
        error instanceof Error ? error.message : 'Unknown error',
        error,
      );
      captureServiceError(serviceError, {
        action: 'uploadMedia',
        step: 'execution',
      });
      return Result.fail(serviceError);
    }
  }

  /**
   * Finalize a browser direct-upload (signed-URL flow). The file is already
   * in storage at `input.storagePath`; this inserts the canonical
   * artist_media row, applies the headshot profile update, and (for videos)
   * returns a signed URL for immediate inline playback.
   *
   * Single edit surface for artist_media inserts: the setup-wizard finalize
   * action previously inlined its own insert payload, which drifted from
   * uploadMedia's (no profiles.profile_photo_url stamp for headshots; the
   * service path lacked professional_profile_id). Both paths now build the
   * row here.
   */
  async finalizeDirectUpload(
    userId: string,
    input: FinalizeDirectUploadInput,
  ): Promise<Result<DancerMedia & { signed_url: string | null }>> {
    const logContext = {
      method: 'finalizeDirectUpload',
      userId,
      mediaType: input.mediaType,
      storagePath: input.storagePath,
    };

    try {
      // Path must start with the caller's own id — the same boundary the
      // storage RLS uses, so a malicious caller can't claim someone else's
      // just-uploaded object. The RPC re-checks it for the row; this early
      // copy guards the storage reads/removals below, which run first.
      if (!input.storagePath.startsWith(`${userId}/`)) {
        this.log('warn', 'Storage path outside caller boundary', logContext);
        return Result.fail(ServiceError.validation('Invalid storage path'));
      }

      // Orphan-row prevention (R2, BAL-595): the bytes were PUT to the signed
      // URL by the browser out-of-band. Verify the object actually landed
      // before inserting the row — a failed/aborted PUT must NOT leave a row
      // pointing at a non-existent object. The same info() read also yields
      // the object's ETag, from which we derive content_hash (BAL-700) — one
      // round-trip does both, no extra storage call.
      // Re-check with bounded backoff: the browser PUT and this server-side
      // `.info()` read hit Supabase Storage over two independent connections,
      // so under concurrent load the object metadata can lag behind a PUT the
      // client already saw succeed (read-after-write). A single miss must not
      // strand the just-uploaded file (BALLEE-G7 — 87 orphan objects during the
      // GDO apply burst 2026-07-09) — fetchStorageObjectHash retries internally.
      const objectInfo = await this.fetchStorageObjectHash(input.storagePath);
      if (!objectInfo.exists) {
        this.log('warn', 'Finalize aborted: uploaded object not found', {
          ...logContext,
        });
        return Result.fail(
          ServiceError.notFound(
            'Uploaded file was not found in storage. Please retry the upload.',
            { reason: 'object_not_found_after_retry' },
          ),
        );
      }
      const contentHash = objectInfo.contentHash;

      // Idempotent re-upload guard (BAL-700): mirrors the uploadMedia guard
      // above. The browser already PUT the bytes to input.storagePath before
      // this call landed; if a live row for this exact
      // (user_id, media_type, file_name) — OR the same bytes (content_hash)
      // under a different name — already exists, remove the just-uploaded
      // duplicate object (best-effort — no row will ever reference it) and
      // return the existing row instead of inserting a second one. Bounded to
      // at most two lookups, no N+1.
      const existingMedia = await this.findExistingUploadedMedia(
        userId,
        input.mediaType,
        input.fileName,
        contentHash,
      );
      if (existingMedia) {
        this.log(
          'info',
          'Duplicate direct upload detected — returning existing row',
          { ...logContext, existingMediaId: existingMedia.id },
        );
        try {
          await this.client.storage
            .from(StorageBuckets.DANCER_MEDIA)
            .remove([input.storagePath]);
        } catch {
          // ignore — cron cleanup sweeps stale objects eventually
        }
        let existingSignedUrl: string | null = null;
        if (existingMedia.storage_path) {
          const existingSignedUrlResult = await this.getSignedUrl(
            existingMedia.storage_path,
          );
          if (existingSignedUrlResult.isSuccess) {
            existingSignedUrl = existingSignedUrlResult.getValue();
          }
        }
        return Result.ok({ ...existingMedia, signed_url: existingSignedUrl });
      }

      // Content check for documents (BAL-747). Step 1 of this flow validates
      // only client-supplied metadata — it runs before the browser has PUT
      // anything — and it now accepts a generic `application/octet-stream` for a
      // `.pdf` name, because some browsers report exactly that for a valid PDF.
      // This is where that relaxation is paid for: the bytes now exist, so the
      // %PDF signature is checked for real. Without it the allowlist change
      // would be cosmetic and any file could enter the document bucket.
      //
      // Deliberately AFTER the dedup guard: a re-upload of bytes we already have
      // is answered from the existing row without paying for a second storage
      // read, and stays idempotent even if that read would have blipped.
      let mimeType = input.mimeType;
      if (input.mediaType === 'document') {
        const head = await this.readStorageHead(
          input.storagePath,
          PDF_SNIFF_BYTES,
        );

        if (!head) {
          // Could not read the object back, even after retries. Distinct from
          // "not a PDF": the file may well be fine, so the object is LEFT IN
          // PLACE rather than deleted — destroying a legitimate CV over a
          // transient storage fault is the worse failure. Re-uploading mints a
          // fresh path, so this object becomes an orphan for the cron sweep;
          // that is the accepted cost of not deleting on an unknown.
          this.log('warn', 'Finalize aborted: document signature unreadable', {
            ...logContext,
          });
          return Result.fail(
            new ServiceError(
              'INTERNAL_ERROR',
              'Could not verify the uploaded document. Please retry the upload.',
              undefined,
              { reason: 'document_signature_unreadable' },
            ),
          );
        }

        // An empty read is a verdict, not an unknown: the stream ended cleanly
        // with no bytes, so whatever landed is not a PDF. It falls through to
        // the rejection below rather than the retry-forever branch above.
        if (!looksLikePdf(head)) {
          // Definitively not a PDF. Remove the object we just accepted so a
          // rejected upload never lingers in the bucket (same orphan-cleanup
          // posture as the duplicate branch above).
          this.log('warn', 'Finalize rejected: document is not a PDF', {
            ...logContext,
            declaredMimeType: input.mimeType,
            headBytes: head.length,
          });
          try {
            await this.client.storage
              .from(StorageBuckets.DANCER_MEDIA)
              .remove([input.storagePath]);
          } catch {
            // Best-effort — cron cleanup sweeps stale objects eventually.
          }
          return Result.fail(
            ServiceError.validation(
              'This file is not a valid PDF. Please upload a PDF document.',
            ),
          );
        }

        // The signature confirmed what the browser could not label. Persist the
        // real type, so the readers that branch on `mime_type === 'application/pdf'`
        // (classifyMediaKind, the media thumbnail, the media picker's pdf alias)
        // treat an octet-stream-reported CV as the document it is.
        mimeType = 'application/pdf';
      }

      // The row contract lives once, in the finalize_dancer_media_upload RPC
      // (BAL-603, Rule 8), shared with the mobile app: column set,
      // professional_profile_id link, is_primary, migrated_from, the 4:5 crop
      // (validated there; an invalid rect is DROPPED, never an error — the
      // public hero letterboxes the full picture), the headshot profile stamp
      // (same transaction as the row), and the Mux 'preparing' stamp.
      const rpcArgs = {
        p_user_id: userId,
        p_media_type: input.mediaType,
        p_storage_path: input.storagePath,
        p_file_name: input.fileName,
        p_file_size_bytes: input.fileSize,
        // Normalised above for documents whose browser-reported type was a
        // generic binary MIME (BAL-747); the client value verbatim otherwise.
        p_mime_type: mimeType,
        p_content_hash: contentHash ?? undefined,
        p_title: input.title ?? undefined,
        p_description: input.description ?? undefined,
        p_category: input.category ?? undefined,
        p_crop: input.crop
          ? {
              x: input.crop.x,
              y: input.crop.y,
              w: input.crop.w,
              h: input.crop.h,
              width: input.crop.width,
              height: input.crop.height,
            }
          : undefined,
        // Native video enters the Mux pipeline immediately (BAL-721 / R4/R5):
        // the RPC stamps 'preparing' AT INSERT, before the ingest byte-copy
        // runs, so a request killed mid-ingest still leaves the row on the
        // mux_status reconciliation index. Set only because this path runs
        // ingestNativeVideoToMux below.
        p_start_mux_ingest: input.mediaType === 'video',
      };

      // Idempotent, bounded-retry write (BAL-696). A transient Supabase
      // timeout can abort the response AFTER Postgres has committed the row;
      // retrying a plain INSERT would then double-insert. The RPC UPSERTs on
      // the (user_id, storage_path) idempotency key so a retry returns the
      // same row instead of a duplicate, and we retry only transient/timeout
      // failures a few times so a brief blip self-heals server-side rather
      // than bouncing to the user (the BAL-127 stopgap did the latter).
      let media: Database['public']['Tables']['artist_media']['Row'] | null =
        null;
      let dbError: { message: string; code?: string } | null = null;

      for (let attempt = 1; attempt <= FINALIZE_MAX_ATTEMPTS; attempt++) {
        const { data, error } = await this.client.rpc(
          'finalize_dancer_media_upload',
          rpcArgs,
        );

        media = data;
        dbError = error;

        if (!error && data) break;

        const transient = isTransientNetworkError(error);
        if (transient && attempt < FINALIZE_MAX_ATTEMPTS) {
          this.log('warn', 'Transient finalize write failure — retrying', {
            ...logContext,
            attempt,
            error: error?.message,
          });
          await sleep(FINALIZE_RETRY_BASE_MS * attempt);
          continue;
        }
        break;
      }

      if (dbError || !media) {
        const transient = isTransientNetworkError(dbError);
        // Non-transient DB rejection → clean rollback: the insert was refused,
        // so the just-uploaded object is a genuine orphan; remove it.
        // Transient exhaustion → DO NOT delete: the row may have committed on a
        // response that timed out, so the object might still own a live row.
        // The idempotent upsert on the user's next retry (or the BAL-657
        // reconciliation cron) reconciles it; deleting here could strand a
        // committed row pointing at a now-missing object.
        if (!transient) {
          try {
            await this.client.storage
              .from(StorageBuckets.DANCER_MEDIA)
              .remove([input.storagePath]);
          } catch {
            // ignore — cron cleanup sweeps stale objects eventually
          }
        }
        this.log('error', 'Database write failed for direct upload', {
          ...logContext,
          error: dbError?.message,
          errorCode: dbError?.code,
          transient,
        });
        return Result.fail(
          ServiceError.database(dbError?.message ?? 'Failed to save media'),
        );
      }

      // Native-video ingest (BAL-721 / R1): a raw .mov/.mp4 dancer upload is not
      // reliably browser-playable, so move the raw object to R2 (the ingest
      // origin, decision D1) and hand its URL to Mux for HLS re-encode. This
      // mutates `media` in place to carry provider/mux_asset_id/mux_status so the
      // finalize response already reflects the 'preparing' state (drives the R4
      // processing UI). Ingest failures are surfaced as mux_status='errored' (R5),
      // never thrown — the upload row + object already exist and must not be lost.
      if (input.mediaType === 'video') {
        await this.ingestNativeVideoToMux(media, userId, logContext);
      }

      // Surface a signed URL for any uploaded file so the wizard can render
      // the thumbnail/preview immediately. dancer-media is a PRIVATE bucket,
      // so the bare storage_path is just a key the browser can't resolve.
      // Previously only videos were signed — a just-uploaded headshot/photo
      // therefore rendered as a placeholder file icon until a page reload
      // picked up the loader's signed URL.
      let signedUrl: string | null = null;
      if (media.storage_path) {
        const signedUrlResult = await this.getSignedUrl(media.storage_path);
        if (signedUrlResult.isSuccess) {
          signedUrl = signedUrlResult.getValue();
        }
      }

      this.log('info', 'Direct upload finalised', {
        ...logContext,
        mediaId: media.id,
      });

      return Result.ok({ ...media, signed_url: signedUrl });
    } catch (error) {
      const serviceError = ServiceError.internal(
        error instanceof Error ? error.message : 'Unknown error',
        error,
      );
      captureServiceError(serviceError, {
        action: 'finalizeDirectUpload',
        step: 'execution',
      });
      return Result.fail(serviceError);
    }
  }

  /**
   * Idempotent-upload guard (BAL-700): look up an existing, live
   * (requires_reupload = false) artist_media row that a new upload would
   * duplicate. Uploaders (uploadMedia, finalizeDirectUpload) call this
   * before inserting so a re-upload of an already-uploaded file returns the
   * existing row instead of creating a duplicate — the only previous
   * uniqueness guard was idx_artist_media_unique_headshot (one headshot per
   * user), which never covered photos, videos, or documents.
   *
   * A row is a duplicate if it matches the same (user_id, media_type) AND
   * either the same file_name OR the same content_hash (identical bytes
   * saved under a different name). The content_hash branch also makes the
   * common same-bytes-rename case return gracefully rather than surfacing
   * as a raw unique-violation from idx_artist_media_unique_content_hash.
   *
   * Excludes requires_reupload rows (Meteor lost-file placeholders) — those
   * genuinely need fresh bytes, not a return of the same dead reference.
   * Bounded to at most two lookups (filename, then hash) — never per-row, so
   * no N+1.
   */
  private async findExistingUploadedMedia(
    userId: string,
    mediaType: string,
    fileName: string,
    contentHash?: string | null,
  ): Promise<Database['public']['Tables']['artist_media']['Row'] | null> {
    const { data: byName } = await this.client
      .from('artist_media')
      .select('*')
      .eq('user_id', userId)
      .eq('media_type', mediaType)
      .eq('file_name', fileName)
      .eq('requires_reupload', false)
      .limit(1)
      .maybeSingle();
    if (byName) return byName;

    if (!contentHash) return null;

    const { data: byHash } = await this.client
      .from('artist_media')
      .select('*')
      .eq('user_id', userId)
      .eq('media_type', mediaType)
      .eq('content_hash', contentHash)
      .eq('requires_reupload', false)
      .limit(1)
      .maybeSingle();
    return byHash ?? null;
  }

  /**
   * Fetch a storage object's existence + byte-content hash in one round-trip
   * (BAL-700 / BAL-595). Uses storage.info(), whose response carries the S3
   * ETag; normalizeContentHash turns a single-PUT ETag into the md5 content
   * hash (and returns null for multipart / non-md5 ETags).
   *
   * Retries with bounded backoff (mirrors MediaWriteService.objectExistsWithRetry,
   * BALLEE-G7): the browser PUT and this server-side `.info()` read reach Supabase
   * Storage over two independent connections, so under concurrent load the object
   * metadata can lag behind a PUT the client already saw succeed (read-after-write).
   * A single miss must not strand the just-uploaded file. A still-missing object
   * after all attempts reports exists=false — the caller fails closed.
   */
  private async fetchStorageObjectHash(
    storagePath: string,
    attempts = 4,
    delayMs = 300,
  ): Promise<{ exists: boolean; contentHash: string | null }> {
    for (let attempt = 0; attempt < attempts; attempt++) {
      if (attempt > 0) {
        await new Promise((resolve) => setTimeout(resolve, delayMs * attempt));
      }
      const { data, error } = await this.client.storage
        .from(StorageBuckets.DANCER_MEDIA)
        .info(storagePath);
      if (!error && data) {
        const rawEtag = data.metadata?.eTag ?? data.etag ?? null;
        return { exists: true, contentHash: normalizeContentHash(rawEtag) };
      }
    }
    return { exists: false, contentHash: null };
  }

  /**
   * Read the first `byteCount` bytes of a stored object (BAL-747).
   *
   * Streams the download and cancels the reader as soon as enough bytes have
   * arrived, so a 10 MB CV costs one chunk off the wire — not a full fetch into
   * the function. Supabase Storage has no range parameter on `download()`, so
   * early cancellation is how the read stays bounded.
   *
   * Retries with the same bounded backoff as {@link fetchStorageObjectHash} and
   * for the same reason: the browser PUT and this server-side read reach Supabase
   * over two independent connections, so under load the object can lag behind a
   * PUT the client already saw succeed (BALLEE-G7 — 87 orphan objects during the
   * GDO apply burst 2026-07-09). Without retries this read would turn that lag
   * into a hard finalize failure for EVERY document, including the ones that
   * already worked before the sniff existed.
   *
   * Retries cover a failed download only. A download that succeeds and yields
   * nothing returns an empty array, not null — an object that streams cleanly to
   * zero bytes is a verdict ("not a PDF"), and retrying it would loop forever.
   * Null means genuinely unreadable, which callers treat as unknown.
   */
  private async readStorageHead(
    storagePath: string,
    byteCount: number,
    attempts = 4,
    delayMs = 300,
  ): Promise<Uint8Array | null> {
    for (let attempt = 0; attempt < attempts; attempt++) {
      if (attempt > 0) {
        await sleep(delayMs * attempt);
      }

      try {
        const { data, error } = await this.client.storage
          .from(StorageBuckets.DANCER_MEDIA)
          .download(storagePath)
          .asStream();

        if (error || !data) continue;

        const reader = data.getReader();
        const chunks: Uint8Array[] = [];
        let total = 0;
        // Zero-length chunks are legal and make no progress, so `total` alone
        // cannot terminate the loop. Bound the reads too.
        let readsLeft = MAX_HEAD_READS;
        // Did the stream end on its own, or did we hit the read cap? Only the
        // former yields a head we can judge — see below.
        let streamEnded = false;

        try {
          while (total < byteCount && readsLeft-- > 0) {
            const { done, value } = await reader.read();
            if (done) {
              streamEnded = true;
              break;
            }
            if (!value?.length) continue;
            chunks.push(value);
            total += value.length;
          }
        } finally {
          // Hang up on the rest of the file. Safe after `done` too — cancelling
          // a closed stream is a no-op.
          await reader.cancel().catch(() => undefined);
        }

        // Hitting the read cap without either filling the sniff window or
        // reaching the end of the stream leaves a SHORT head. That is not a
        // verdict — the caller deletes the object on a head that fails the
        // signature check, and `%PDF-` may simply lie past what we read. Report
        // it as unknown so a legitimate CV is never destroyed by a pathological
        // chunk pattern.
        if (total < byteCount && !streamEnded) return null;

        const head = new Uint8Array(Math.min(total, byteCount));
        let written = 0;
        for (const chunk of chunks) {
          if (written >= head.length) break;
          const take = Math.min(chunk.length, head.length - written);
          head.set(chunk.subarray(0, take), written);
          written += take;
        }
        return head;
      } catch {
        // Transient — fall through to the next attempt.
      }
    }

    return null;
  }

  /**
   * Native-video → Mux ingest (BAL-721, decision D1 / R1).
   *
   * Copies the just-finalized raw video out of the private DANCER_MEDIA bucket
   * into R2 (the vendor-agnostic ingest origin) and creates a Mux asset from the
   * R2 URL, requesting free Whisper captions (Rule 11). The Mux `passthrough` is
   * the `artist_media.id`, so the `video.asset.ready` webhook reconciles the row
   * (writing `mux_playback_id`) without a lookup table.
   *
   * Robustness: this NEVER throws out of finalize. Any failure (storage read, R2
   * write, Mux create, or the persist) is recorded as `mux_status='errored'` so
   * it surfaces on the reconciliation index (R5) instead of silently leaving a
   * broken tile. `media` is mutated in place so the finalize response carries the
   * ingest state.
   *
   * The Mux SDK is never touched here — all vendor calls go through the injected
   * {@link VideoService} (Rule 11 / R7); R2 access goes through {@link R2Service}.
   */
  private async ingestNativeVideoToMux(
    media: Database['public']['Tables']['artist_media']['Row'],
    userId: string,
    logContext: Record<string, unknown>,
  ): Promise<void> {
    // Only native uploads (storage-backed, no external host) are ingested.
    if (!media.storage_path || media.external_url) return;

    const flagErrored = async (
      reason: string,
      detail?: unknown,
      extra?: Record<string, unknown>,
    ): Promise<void> => {
      this.log('error', `Mux ingest failed: ${reason}`, {
        ...logContext,
        mediaId: media.id,
        detail: detail instanceof Error ? detail.message : detail,
      });
      try {
        await this.client
          .from('artist_media')
          .update({ mux_status: 'errored', ...extra })
          .eq('id', media.id);
      } catch {
        // Best-effort — the reconciliation index / retry sweep resweeps.
      }
      media.mux_status = 'errored';
      if (extra?.r2_key && typeof extra.r2_key === 'string') {
        media.r2_key = extra.r2_key;
      }
    };

    try {
      const r2 = this.getR2Service();
      const video = this.getVideoService();

      // 1. Pull the raw bytes from the private DANCER_MEDIA bucket.
      const download = await this.client.storage
        .from(StorageBuckets.DANCER_MEDIA)
        .download(media.storage_path);
      if (download.error || !download.data) {
        await flagErrored('storage download failed', download.error);
        return;
      }
      const bytes = Buffer.from(await download.data.arrayBuffer());

      // 2. Write it to R2 under a deterministic, URL-safe key (Rule 11: R2 is
      //    the origin). The key is derived from media.id + a mime-based
      //    extension — NEVER the raw file_name, which is user-controlled and
      //    unencoded: getPublicUrl does no escaping, so a space / '#' / '?' /
      //    non-ASCII char in file_name would truncate or malform the URL handed
      //    to Mux and fail the fetch. media.id already guarantees uniqueness.
      const ext = VIDEO_MIME_EXT[media.mime_type ?? ''] ?? DEFAULT_VIDEO_EXT;
      const r2Key = `dancer-videos/${userId}/${media.id}/source.${ext}`;
      const upload = await r2.upload(r2Key, bytes, {
        contentType: media.mime_type ?? 'video/mp4',
      });
      if (upload.isFailure) {
        await flagErrored('r2 upload failed', upload.getError());
        return;
      }

      // 3. Hand the public R2 URL to Mux. passthrough = artist_media.id lets the
      //    webhook reconcile the row; Whisper captions are free (Rule 11).
      const asset = await video.createAssetFromUrl({
        url: r2.getPublicUrl(r2Key),
        passthrough: media.id,
        generateSubtitles: true,
      });
      if (asset.isFailure) {
        // Record the R2 key we already wrote so a retry doesn't re-copy.
        await flagErrored('mux asset create failed', asset.getError(), {
          r2_key: r2Key,
        });
        return;
      }

      // 4. Persist the ingest state. The playback id arrives later on the webhook.
      const created = asset.getValue();
      const { error } = await this.client
        .from('artist_media')
        .update({
          provider: 'mux',
          r2_key: r2Key,
          mux_asset_id: created.assetId,
          mux_status: 'preparing',
        })
        .eq('id', media.id);
      if (error) {
        await flagErrored('persist ingest state failed', error, {
          r2_key: r2Key,
        });
        return;
      }

      media.provider = 'mux';
      media.r2_key = r2Key;
      media.mux_asset_id = created.assetId;
      media.mux_status = 'preparing';
      this.log('info', 'Mux ingest started', {
        ...logContext,
        mediaId: media.id,
        muxAssetId: created.assetId,
      });
    } catch (error) {
      await flagErrored('unexpected ingest error', error);
    }
  }

  /**
   * Add video link (YouTube, Vimeo, etc.)
   */
  async addVideoLink(
    userId: string,
    input: VideoLinkInput,
  ): Promise<Result<DancerMedia>> {
    try {
      // Validate URL format. Pasted links often carry surrounding spaces or
      // an http:// scheme; both are normalized before the check.
      const url = normalizeExternalVideoUrl(input.url);
      if (!isValidExternalVideoUrl(url)) {
        return Result.fail(
          ServiceError.validation(
            'Invalid video URL. Must be YouTube or Vimeo.',
          ),
        );
      }

      // Check video count limit (max 5)
      const { count, error: countError } = await this.client
        .from('artist_media')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('media_type', 'video');

      if (countError) {
        return Result.fail(
          ServiceError.database(countError.message, countError),
        );
      }

      if (count && count >= 5) {
        return Result.fail(
          ServiceError.validation('Maximum 5 videos allowed per profile.'),
        );
      }

      // Resolve a display title (+ cached thumbnail) from the provider's
      // oEmbed API so the clip never renders as "Untitled clip". Prefer a
      // user-supplied title; fall back to the oEmbed title. Best-effort: a
      // slow/failed fetch (3s timeout) just leaves nulls. `file_name` is
      // the canonical display field for both uploads and links (the public
      // profile + categorisation grid read it); keep `title` in sync for
      // the editorial metadata editor.
      const meta = await fetchVideoMeta(url);
      const resolvedTitle =
        input.title?.trim() || meta.title?.slice(0, 200) || null;

      // The public profile loads media by professional_profile_id, not
      // user_id. Without this link the clip is saved, listed in the dancer's
      // own settings, and absent from the public profile (88 rows hidden this
      // way, 2026-05 to 2026-10). Same lookup as finalize_dancer_media_upload.
      const { data: professionalProfile, error: profileError } =
        await this.client
          .from('professional_profiles')
          .select('id')
          .eq('user_id', userId)
          .eq('profile_type', 'dancer')
          .limit(1)
          .maybeSingle();

      if (profileError) {
        return Result.fail(
          ServiceError.database(profileError.message, profileError),
        );
      }

      // Create video record
      const insertPayload = {
        user_id: userId,
        professional_profile_id: professionalProfile?.id ?? null,
        media_type: 'video' as MediaType,
        external_url: url,
        file_name: resolvedTitle,
        title: resolvedTitle,
        description: input.description || null,
        thumbnail_url: meta.thumbnailUrl,
      };

      const { data: media, error: dbError } = await this.client
        .from('artist_media')
        .insert(insertPayload)
        .select()
        .single();

      if (dbError) {
        return Result.fail(ServiceError.database(dbError.message, dbError));
      }

      return Result.ok(media);
    } catch (error) {
      const serviceError = ServiceError.internal(
        error instanceof Error ? error.message : 'Unknown error',
        error,
      );
      captureServiceError(serviceError, {
        action: 'addVideoLink',
        step: 'execution',
      });
      return Result.fail(serviceError);
    }
  }

  /**
   * Get all media for a user with signed URLs for images
   */
  async getMediaByUser(userId: string): Promise<Result<DancerMedia[]>> {
    try {
      const { data, error } = await this.client
        .from('artist_media')
        .select('*')
        .eq('user_id', userId)
        .order('display_order', { ascending: true })
        .order('created_at', { ascending: false });

      if (error) {
        return Result.fail(ServiceError.database(error.message, error));
      }

      if (!data || data.length === 0) {
        return Result.ok([]);
      }

      // Sign all media with a storage_path in ONE batch round-trip (no
      // per-row sign loop). Videos added as YouTube/Vimeo links
      // (storage_path null, external_url set) skip this step; uploaded
      // videos (storage_path set) get a signed URL just like images.
      const pathsToSign = data
        .map((item) => item.storage_path)
        .filter((p): p is string => !!p);

      const signedByPath = new Map<string, string>();
      if (pathsToSign.length > 0) {
        const batch = await this.storageService.getBatchSignedUrls(
          StorageBuckets.DANCER_MEDIA,
          pathsToSign,
          { expiresIn: SignedUrlExpiry.IMMEDIATE_DISPLAY },
        );
        for (const { path, signedUrl } of batch) {
          if (signedUrl) signedByPath.set(path, signedUrl);
        }
      }

      const mediaWithUrls = data.map((item) => {
        const signed = item.storage_path
          ? signedByPath.get(item.storage_path)
          : undefined;
        return signed ? { ...item, storage_path: signed } : item;
      });

      return Result.ok(mediaWithUrls);
    } catch (error) {
      const serviceError = ServiceError.internal(
        error instanceof Error ? error.message : 'Unknown error',
        error,
      );
      captureServiceError(serviceError, {
        action: 'getMediaByUser',
        step: 'execution',
      });
      return Result.fail(serviceError);
    }
  }

  /**
   * Get headshot for a user
   */
  async getHeadshot(userId: string): Promise<Result<DancerMedia | null>> {
    try {
      const { data, error } = await this.client
        .from('artist_media')
        .select('*')
        .eq('user_id', userId)
        .eq('media_type', 'headshot')
        .maybeSingle();

      if (error) {
        return Result.fail(ServiceError.database(error.message, error));
      }

      return Result.ok(data);
    } catch (error) {
      const serviceError = ServiceError.internal(
        error instanceof Error ? error.message : 'Unknown error',
        error,
      );
      captureServiceError(serviceError, {
        action: 'getHeadshot',
        step: 'execution',
      });
      return Result.fail(serviceError);
    }
  }

  /**
   * Update media metadata (title, description, editorial category).
   *
   * `category` is the spec-vocabulary editorial bucket. Pair-validation
   * (photo vs video values) is enforced at the DB layer by
   * `artist_media_category_check`; passing an invalid pair returns a
   * `Result.fail` with the constraint message.
   */
  async updateMedia(
    mediaId: string,
    input: {
      title?: string | null;
      description?: string | null;
      category?: string | null;
    },
  ): Promise<Result<DancerMedia>> {
    try {
      const updates = buildMediaMetadataUpdates(input);

      const { data, error } = await this.client
        .from('artist_media')
        .update(updates)
        .eq('id', mediaId)
        .select()
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return Result.fail(ServiceError.notFound('Media not found'));
        }
        return Result.fail(ServiceError.database(error.message, error));
      }

      return Result.ok(data);
    } catch (error) {
      const serviceError = ServiceError.internal(
        error instanceof Error ? error.message : 'Unknown error',
        error,
      );
      captureServiceError(serviceError, {
        action: 'updateMedia',
        step: 'execution',
      });
      return Result.fail(serviceError);
    }
  }

  /**
   * Delete media from storage and database
   */
  async deleteMedia(mediaId: string, userId: string): Promise<Result<void>> {
    try {
      // Fetch media details first
      const { data: media, error: fetchError } = await this.client
        .from('artist_media')
        .select('*')
        .eq('id', mediaId)
        .eq('user_id', userId)
        .single();

      if (fetchError) {
        if (fetchError.code === 'PGRST116') {
          return Result.fail(ServiceError.notFound('Media not found'));
        }
        return Result.fail(
          ServiceError.database(fetchError.message, fetchError),
        );
      }

      // Delete from storage (if not a video)
      if (media.storage_path) {
        const { error: storageError } = await this.client.storage
          .from(StorageBuckets.DANCER_MEDIA)
          .remove([media.storage_path]);

        if (storageError) {
          this.log('warn', 'Failed to delete file from storage', {
            error: storageError,
            storagePath: media.storage_path,
          });
          // Continue with database deletion even if storage fails
        }
      }

      // Delete from database
      const { error: deleteError } = await this.client
        .from('artist_media')
        .delete()
        .eq('id', mediaId);

      if (deleteError) {
        return Result.fail(
          ServiceError.database(deleteError.message, deleteError),
        );
      }

      // If headshot was deleted, clear profile photo URL and completion status
      if (media.media_type === 'headshot') {
        await this.client
          .from('profiles')
          .update({
            has_completed_media_step: false,
            profile_photo_url: null,
          })
          .eq('id', userId);
      }

      return Result.ok(undefined);
    } catch (error) {
      const serviceError = ServiceError.internal(
        error instanceof Error ? error.message : 'Unknown error',
        error,
      );
      captureServiceError(serviceError, {
        action: 'deleteMedia',
        step: 'execution',
      });
      return Result.fail(serviceError);
    }
  }

  /**
   * Get a signed URL for viewing/downloading media
   *
   * Uses centralized StorageUrlService for consistent URL generation.
   */
  async getSignedUrl(
    storagePath: string,
    expiresIn: number = SignedUrlExpiry.IMMEDIATE_DISPLAY,
  ): Promise<Result<string>> {
    return this.storageService.getSignedUrl(
      StorageBuckets.DANCER_MEDIA,
      storagePath,
      { expiresIn },
    );
  }

  /**
   * Batch-sign multiple dancer-media storage paths in ONE round-trip.
   *
   * Returns a `path → signedUrl` map; paths that fail to sign (e.g. RLS denies,
   * object missing) are simply omitted, so callers keep their existing value.
   * Signing is RLS-enforced by Supabase Storage, so only objects the caller can
   * SELECT are returned. Prefer this over N single `getSignedUrl` calls.
   */
  async getSignedUrls(
    storagePaths: string[],
    expiresIn: number = SignedUrlExpiry.IMMEDIATE_DISPLAY,
  ): Promise<Map<string, string>> {
    const map = new Map<string, string>();
    if (storagePaths.length === 0) return map;

    const batch = await this.storageService.getBatchSignedUrls(
      StorageBuckets.DANCER_MEDIA,
      storagePaths,
      { expiresIn },
    );
    for (const { path, signedUrl } of batch) {
      if (signedUrl) map.set(path, signedUrl);
    }
    return map;
  }

  /**
   * Persist a new display order for a set of media rows owned by `userId`.
   *
   * `items` is the new ordered list. The first item gets `display_order = 0`,
   * the second `1`, and so on. Each row update is gated on `user_id` so RLS
   * prevents cross-user writes even if `mediaId`s leak.
   */
  async reorder(
    userId: string,
    items: { id: string }[],
  ): Promise<Result<void>> {
    if (items.length === 0) return Result.ok(undefined);

    try {
      const updates = items.map((item, index) =>
        this.client
          .from('artist_media')
          .update({ display_order: index })
          .eq('id', item.id)
          .eq('user_id', userId),
      );

      const results = await Promise.all(updates);
      const firstError = results.find((r) => r.error)?.error;
      if (firstError) {
        return Result.fail(
          ServiceError.database(firstError.message, firstError),
        );
      }
      return Result.ok(undefined);
    } catch (error) {
      const serviceError = ServiceError.internal(
        error instanceof Error ? error.message : 'Unknown error',
        error,
      );
      captureServiceError(serviceError, {
        action: 'reorder',
        step: 'execution',
      });
      return Result.fail(serviceError);
    }
  }
}
